// Праздничный экран при выполнении задания.
// Монтируется один раз (index.js) и слушает события из celebrate.js:
//   earned  — фейерверк, крупная «золотая» сумма, затем сумма рассыпается
//             на монеты, которые летят в баланс в шапке;
//   pending — отчёт отправлен: галочка, крупное «Отчёт отправлен», маленький салют;
//             без суммы — за отчёт пока ничего не начислено;
//   synced  — компактное «+N», влетающее в баланс (Instagram / TikTok).
// Пока экран затемнён, тап по нему закрывает праздник (и не доходит до
// невидимых под затемнением кнопок); после ухода затемнения слой касания
// не перехватывает. При «уменьшении движения» в системе — без салюта
// и полётов, только плавное появление суммы.
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createFireworks, FIREWORK_PALETTES } from './fireworks';
import useAnimatedNumber from './useAnimatedNumber';
import {
    CELEBRATE_EVENT, TIMING, prefersReducedMotion, toDisplayAmount,
    formatAmount, amountDecimals, currencyLabel, setCoinsArrival
} from './celebrate';
import './celebration.css';

const GOLD = FIREWORK_PALETTES[0];
const MINT = FIREWORK_PALETTES[1];
const EXIT_MS = 420;            // анимация ухода текста и затемнения
const MAX_TAIL_MS = 3200;       // сколько после ухода ещё могут догорать искры

const isLowEnd = () => {
    try {
        return (navigator.hardwareConcurrency || 8) <= 4 || (navigator.deviceMemory || 8) <= 2;
    } catch (e) {
        return false;
    }
};

// 💎 и 🍊 летят как есть, «₽» — внутри золотой монетки
const isEmoji = (label) => !!label && label !== '₽' && label.charCodeAt(0) > 0x2000;

// Видимый баланс в шапке — туда летят монеты
function findBalanceRect() {
    const el = document.querySelector('.header_money');
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (r.width < 1 || r.height < 1 || r.bottom < 0 || r.top > window.innerHeight) return null;
    return r;
}

function centerOf(el) {
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

// Монеты из суммы → в баланс. onLand(x, y) вызывается, когда долетела первая
// (x, y = null, если баланса в шапке не видно и монеты просто улетают вверх).
// Возвращает false, если летать нечему или браузер не умеет Element.animate.
function flyCoins(from, label, count, onLand) {
    if (!from || !document.body || typeof document.body.animate !== 'function') return false;
    const target = findBalanceRect();
    const tx = target ? target.left + target.width / 2 : from.x;
    const ty = target ? target.top + target.height / 2 : -60;
    const layer = document.createElement('div');
    layer.className = 'celebration-coins';
    document.body.appendChild(layer);
    const disc = !isEmoji(label);
    let alive = count;
    let landed = false;
    const finish = (coin) => {
        if (coin.parentNode) coin.parentNode.removeChild(coin);
        if (!landed) {
            landed = true;
            if (onLand) onLand(target ? tx : null, target ? ty : null);
        }
        alive -= 1;
        if (alive <= 0 && layer.parentNode) layer.parentNode.removeChild(layer);
    };
    for (let i = 0; i < count; i++) {
        const coin = document.createElement('span');
        coin.className = disc ? 'celebration-coin celebration-coin--disc' : 'celebration-coin';
        coin.textContent = label;
        coin.style.left = from.x + 'px';
        coin.style.top = from.y + 'px';
        layer.appendChild(coin);
        // сначала монеты «выпрыгивают» веером, потом с ускорением летят в шапку
        const px = (Math.random() - 0.5) * 220;
        const py = -30 - Math.random() * 90;
        const rot = (Math.random() - 0.5) * 160;
        const dx = tx - from.x;
        const dy = ty - from.y;
        try {
            const anim = coin.animate([
                { transform: 'translate(-50%, -50%) translate(0px, 0px) scale(0.3) rotate(0deg)', opacity: 0, easing: 'cubic-bezier(.2, .8, .3, 1)' },
                { transform: 'translate(-50%, -50%) translate(' + px + 'px, ' + py + 'px) scale(1.15) rotate(' + (rot / 2) + 'deg)', opacity: 1, offset: 0.34, easing: 'cubic-bezier(.55, 0, .85, .4)' },
                { transform: 'translate(-50%, -50%) translate(' + dx + 'px, ' + dy + 'px) scale(' + (target ? 0.45 : 0.8) + ') rotate(' + rot + 'deg)', opacity: target ? 1 : 0 }
            ], { duration: TIMING.coinFlight, delay: i * 45, fill: 'both' });
            anim.onfinish = () => finish(coin);
            anim.oncancel = () => finish(coin);
        } catch (e) {
            finish(coin);
        }
    }
    // страховка: слой не должен остаться в DOM ни при каких условиях
    window.setTimeout(() => {
        if (layer.parentNode) layer.parentNode.removeChild(layer);
    }, TIMING.coinFlight + count * 45 + 800);
    return true;
}

function CheckIcon({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 12.5l4.2 4.2L19 7" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

// Сумма с «прокруткой» цифр
function Amount({ rubles, label, firstShow, reduced, numRef, size }) {
    const target = toDisplayAmount(rubles);
    const decimals = amountDecimals(target);
    const anim = useAnimatedNumber(target, {
        from: 0,
        duration: firstShow ? 950 : 600,
        delay: firstShow ? 200 : 0,
        disabled: reduced,
        bumpMs: 500
    });
    // длинные суммы — шрифт поменьше, чтобы влезали в узкий экран
    const len = formatAmount(target, decimals).length + 1;
    const lenClass = size !== 'big' ? '' : (len > 8 ? ' is-xlong' : (len > 5 ? ' is-long' : ''));
    return (
        <div className={'celebration__amount celebration__amount--' + size + lenClass + (anim.phase === 'done' ? ' is-done' : '')}>
            <span ref={numRef} className="celebration__num">+{formatAmount(anim.value, decimals)}</span>
            {isEmoji(label)
                ? <span className="celebration__cur">{label}</span>
                : <span className="celebration__cur celebration__cur--text">{label}</span>}
        </div>
    );
}

export default function Celebration({ isUpmob }) {
    const [cel, setCel] = useState(null);
    const [announce, setAnnounce] = useState('');
    const celRef = useRef(null);
    const fwRef = useRef(null);
    const canvasRef = useRef(null);
    const numRef = useRef(null);
    const markRef = useRef(null);
    const seqRef = useRef(0);
    const timers = useRef({ exit: 0, tail: 0, exitAt: 0 });
    const labelRef = useRef('');
    const landedRef = useRef(true);       // монеты текущего большого праздника уже в шапке
    const dismissRef = useRef(null);      // закрыть праздник тапом по затемнению
    labelRef.current = currencyLabel(isUpmob);

    useEffect(() => {
        const t = timers.current;
        const update = (next) => {
            celRef.current = next;
            setCel(next);
        };
        const clear = (name) => {
            window.clearTimeout(t[name]);
            window.clearInterval(t[name]);
            t[name] = 0;
        };
        const unmount = () => {
            clear('exit'); clear('tail');
            update(null);
        };
        // Уход: текст и затемнение исчезают, монеты летят в шапку, салют догорает.
        const beginExit = () => {
            const cur = celRef.current;
            if (!cur || cur.exiting) return;
            clear('exit');
            if (cur.kind === 'earned') {
                const fw = fwRef.current;
                const total = toDisplayAmount(cur.total);
                const count = Math.max(5, Math.min(10, 4 + Math.round(Math.log(total + 1) / Math.LN2)));
                const flying = !cur.reduced && flyCoins(centerOf(numRef.current), labelRef.current, count, (x, y) => {
                    // первая монета в шапке: баланс начинает «докручиваться»
                    // (если за это время начался новый праздник — его монеты ещё впереди)
                    const now = celRef.current;
                    if (!now || now.id === cur.id) {
                        landedRef.current = true;
                        setCoinsArrival(0);
                    }
                    if (x !== null && fwRef.current === fw && fw) fw.burst(x, y, GOLD, 'ring', 0.32);
                });
                if (flying) {
                    setCoinsArrival(TIMING.coinFlight);
                } else {
                    landedRef.current = true;
                    setCoinsArrival(0);
                }
            }
            update({ ...cur, exiting: true });
            // окончательно убираем слой, когда догорят искры (но не позже MAX_TAIL_MS)
            const startedAt = Date.now();
            clear('tail');
            t.tail = window.setInterval(() => {
                const fw = fwRef.current;
                const passed = Date.now() - startedAt;
                if (passed >= EXIT_MS + TIMING.coinFlight && (!fw || fw.isIdle() || passed > MAX_TAIL_MS)) {
                    unmount();
                }
            }, 200);
        };
        const scheduleExit = (ms) => {
            clear('exit');
            t.exit = window.setTimeout(beginExit, ms);
            t.exitAt = Date.now() + ms;
        };

        const onCelebrate = (e) => {
            const d = (e && e.detail) || {};
            const kind = d.kind === 'pending' || d.kind === 'synced' ? d.kind : 'earned';
            const amount = parseFloat(d.amount) || 0;
            if (!(amount > 0) && kind !== 'pending') return;
            const cur = celRef.current;
            // Складываем: ещё одно начисление, пока сумма на экране; «отчёт» к «отчёту»;
            // компактное «+N», пока монеты большого праздника не долетели до шапки
            // (шапка в этом случае ждёт те же монеты — см. celebrate.js).
            // Два компактных подряд не складываются: каждое «+N» показывается заново.
            const canMerge = !!cur && (
                (kind === 'synced' && cur.kind === 'earned' && !landedRef.current) ||
                (!cur.exiting && kind !== 'synced' && cur.kind === kind)
            );
            if (canMerge) {
                update({ ...cur, total: cur.total + amount, merges: cur.merges + 1, title: cur.title && d.title ? cur.title : '' });
                const fw = fwRef.current;
                if (kind === 'earned') {
                    // ещё одно задание засчитано, пока идёт праздник: добавляем залпов и время
                    if (fw) fw.sprinkle(2, 240, 0);
                    if ((t.exitAt || 0) - Date.now() < TIMING.mergeStay) scheduleExit(TIMING.mergeStay);
                }
            } else {
                clear('exit'); clear('tail');
                landedRef.current = kind !== 'earned';
                seqRef.current += 1;
                const next = {
                    id: seqRef.current,
                    kind: kind,
                    total: amount,
                    merges: 0,
                    title: typeof d.title === 'string' ? d.title : '',
                    exiting: false,
                    reduced: prefersReducedMotion(),
                    anchor: kind === 'synced' ? findBalanceRect() : null
                };
                update(next);
                if (kind === 'earned') scheduleExit(next.reduced ? 2200 : TIMING.earnedStay);
                else if (kind === 'pending') scheduleExit(next.reduced ? 2400 : TIMING.pendingStay);
                else scheduleExit(TIMING.syncedArrive + 500);
            }
            if (kind === 'pending') {
                setAnnounce('Отчёт отправлен. Вознаграждение — после проверки');
                return;
            }
            const now = celRef.current;
            const text = '+' + formatAmount(toDisplayAmount(now.total)) + ' ' + labelRef.current;
            setAnnounce('Начислено ' + text);
        };

        dismissRef.current = beginExit;
        window.addEventListener(CELEBRATE_EVENT, onCelebrate);
        return () => {
            window.removeEventListener(CELEBRATE_EVENT, onCelebrate);
            dismissRef.current = null;
            clear('exit'); clear('tail');
        };
    }, []);

    // Новый праздник: создаём салют на свежем canvas и запускаем залпы
    const celId = cel ? cel.id : 0;
    useEffect(() => {
        const cur = celRef.current;
        if (!cur || cur.reduced || !canvasRef.current) return undefined;
        const fw = createFireworks(canvasRef.current, { density: isLowEnd() ? 0.6 : 1 });
        if (!fw) return undefined;
        fwRef.current = fw;
        const local = [];
        const at = (ms, fn) => local.push(window.setTimeout(fn, ms));
        const size = fw.size();
        if (cur.kind === 'earned') {
            // чем больше сумма, тем богаче салют: 3–6 разрывов вокруг суммы + 2 ракеты
            const total = toDisplayAmount(cur.total);
            const bursts = Math.max(3, Math.min(6, 3 + Math.floor(Math.log(total + 1) / Math.LN10)));
            at(120, () => {
                const c = centerOf(numRef.current) || { x: size.width / 2, y: size.height * 0.4 };
                fw.burst(c.x, c.y, GOLD, 'ring', 1);
            });
            fw.sprinkle(bursts, 270, 330);
            fw.show(2, 340);
        } else if (cur.kind === 'pending') {
            at(420, () => {
                const c = centerOf(markRef.current) || { x: size.width / 2, y: size.height * 0.4 };
                fw.burst(c.x, c.y, MINT, 'ring', 0.55);
                fw.burst(c.x, c.y, GOLD, 'peony', 0.4);
            });
        } else {
            at(TIMING.syncedArrive, () => {
                const r = findBalanceRect();
                const x = r ? r.left + r.width / 2 : size.width / 2;
                const y = r ? r.top + r.height / 2 : 40;
                fw.burst(x, y, GOLD, 'ring', 0.3);
            });
        }
        return () => {
            local.forEach((id) => window.clearTimeout(id));
            fw.destroy();
            if (fwRef.current === fw) fwRef.current = null;
        };
    }, [celId]);

    // live-регион всегда в DOM, чтобы экранный диктор озвучил начисление
    const live = <div className="celebration-sr" role="status" aria-live="polite">{announce}</div>;
    if (!cel) {
        return createPortal(<React.Fragment>{live}{null}</React.Fragment>, document.body);
    }

    const label = currencyLabel(isUpmob);
    const rootClass = 'celebration celebration--' + cel.kind +
        (cel.exiting ? ' is-exiting' : '') + (cel.reduced ? ' is-reduced' : '');

    let content = null;
    if (cel.kind === 'earned') {
        content = (
            <div className="celebration__stage">
                <div className="celebration__rays" />
                <div className="celebration__glow" />
                <div className="celebration__label">
                    <CheckIcon className="celebration__label-icon" />
                    {cel.title ? cel.title : cel.merges > 0 ? 'Задания выполнены!' : 'Задание выполнено!'}
                </div>
                <Amount rubles={cel.total} label={label} firstShow={cel.merges === 0} reduced={cel.reduced} numRef={numRef} size="big" />
                <div className="celebration__caption">Начислено на баланс</div>
            </div>
        );
    } else if (cel.kind === 'pending') {
        content = (
            <div className="celebration__stage">
                <div className="celebration__glow celebration__glow--mint" />
                <div ref={markRef} className="celebration__mark">
                    <CheckIcon className="celebration__mark-icon" />
                </div>
                <div className="celebration__headline">{cel.merges > 0 ? 'Отчёты отправлены' : 'Отчёт отправлен'}</div>
                <div className="celebration__caption">Вознаграждение — после проверки</div>
            </div>
        );
    } else {
        const a = cel.anchor;
        const style = a
            ? { left: (a.left + a.width / 2) + 'px', top: (a.bottom + 10) + 'px', '--rise': -(a.height / 2 + 10 + 14) + 'px' }
            : { left: '50%', top: '72px', '--rise': '-48px' };
        content = (
            <div className="celebration__chip" style={style}>
                +{formatAmount(toDisplayAmount(cel.total))}<span className="celebration__chip-cur">{label}</span>
            </div>
        );
    }

    return createPortal(
        <React.Fragment>
            {live}
            <div key={cel.id} className={rootClass} aria-hidden="true">
                {cel.kind !== 'synced' &&
                    <div className="celebration__backdrop" onClick={() => dismissRef.current && dismissRef.current()} />}
                {!cel.reduced && <canvas ref={canvasRef} className="celebration__canvas" />}
                {content}
            </div>
        </React.Fragment>,
        document.body
    );
}
